import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabase';

const ResetPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [hasActiveSession, setHasActiveSession] = useState<boolean>(false);

  useEffect(() => {
    const verifyResetToken = async () => {
      // First, check if user has an active session (might have been auto-logged in)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        console.log('✅ User has active session, allowing password reset');
        setHasActiveSession(true);
        setIsValidToken(true);
        // Clear any error messages since user is authenticated
        setMessage('');
        return;
      }
      
    // Check if we have a valid reset token in the URL or error
    const hash = window.location.hash;
      const searchParams = new URLSearchParams(window.location.search);
    
      // Check for errors in hash parameters
    if (hash.includes('error=')) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const error = hashParams.get('error');
      const errorCode = hashParams.get('error_code');
      const errorDescription = hashParams.get('error_description');
      
        console.error('❌ Reset password error detected in hash:', { error, errorCode, errorDescription });
        
        // Check if user has an active session despite the error
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user) {
          console.log('✅ User has active session despite error, allowing password reset');
          setHasActiveSession(true);
          setIsValidToken(true);
          setMessage('');
          return;
        }
      
      setIsValidToken(false);
      
      let errorMsg = 'Invalid or expired reset link. ';
        if (errorCode === 'otp_expired' || errorCode === 'token_expired') {
          errorMsg = 'This password reset link has expired. Password reset links are valid for 1 hour. Please request a new password reset.';
        } else if (errorDescription) {
          errorMsg = decodeURIComponent(errorDescription);
        } else if (error === 'access_denied') {
          errorMsg = 'Access denied. This reset link is invalid or has already been used. Please request a new password reset.';
        } else {
          errorMsg += 'Please request a new password reset link.';
        }
        
        setMessage(errorMsg);
        // Sign out any partial session
        await supabase.auth.signOut();
        return;
      }
      
      // Check for errors in query parameters
      if (searchParams.has('error') || searchParams.has('error_code')) {
        const error = searchParams.get('error');
        const errorCode = searchParams.get('error_code');
        const errorDescription = searchParams.get('error_description');
        
        console.error('❌ Reset password error detected in query params:', { error, errorCode, errorDescription });
        
        // Check if user has an active session despite the error
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user) {
          console.log('✅ User has active session despite error, allowing password reset');
          setHasActiveSession(true);
          setIsValidToken(true);
          setMessage('');
          return;
        }
        
        setIsValidToken(false);
        
        let errorMsg = 'Invalid or expired reset link. ';
        if (errorCode === 'otp_expired' || errorCode === 'token_expired') {
        errorMsg = 'This password reset link has expired. Password reset links are valid for 1 hour. Please request a new password reset.';
      } else if (errorDescription) {
        errorMsg = decodeURIComponent(errorDescription);
      } else if (error === 'access_denied') {
        errorMsg = 'Access denied. This reset link is invalid or has already been used. Please request a new password reset.';
      } else {
        errorMsg += 'Please request a new password reset link.';
      }
      
      setMessage(errorMsg);
        // Sign out any partial session
        await supabase.auth.signOut();
      return;
    }
    
      // Check for valid reset token in hash
    const hasAccessToken = hash.includes('access_token') || hash.includes('type=recovery');
    
    if (hasAccessToken) {
        console.log('✅ Reset password token detected in URL, verifying...');
        
        // Try to extract and verify the token by setting a session
        try {
          const hashMatch = hash.match(/#([^#]*)$/);
          if (hashMatch && hashMatch[1]) {
            const params = new URLSearchParams(hashMatch[1]);
            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');
            const type = params.get('type');
            
            // Check if this is a recovery token
            if (type === 'recovery' && accessToken && refreshToken) {
              // Try to set the session to verify the token is still valid
              const { data: { session }, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
              
              if (sessionError) {
                console.error('❌ Token verification failed:', sessionError);
                
                // Check if user somehow has an active session despite token error
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                if (currentSession?.user) {
                  console.log('✅ User has active session despite token error, allowing password reset');
                  setHasActiveSession(true);
                  setIsValidToken(true);
                  setMessage('');
                  return;
                }
                
                setIsValidToken(false);
                
                // Check for specific error codes
                const errorCode = (sessionError as any).code || sessionError.message;
                if (errorCode?.includes('expired') || errorCode?.includes('invalid') || sessionError.message?.includes('expired')) {
                  setMessage('This password reset link has expired. Password reset links are valid for 1 hour. Please request a new password reset.');
                } else {
                  setMessage('Invalid or expired reset link. Please request a new password reset.');
                }
                
                // Sign out any partial session
                await supabase.auth.signOut();
                return;
              }
              
              if (session && session.user) {
                console.log('✅ Token verified successfully');
                setHasActiveSession(true);
                setIsValidToken(true);
                return;
              }
            } else if (accessToken && refreshToken) {
              // Token present but type might not be specified - still try to verify
              const { data: { session }, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
              
              if (sessionError) {
                console.error('❌ Token verification failed:', sessionError);
                
                // Check if user somehow has an active session despite token error
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                if (currentSession?.user) {
                  console.log('✅ User has active session despite token error, allowing password reset');
                  setHasActiveSession(true);
                  setIsValidToken(true);
                  setMessage('');
                  return;
                }
                
                setIsValidToken(false);
                const errorCode = (sessionError as any).code || sessionError.message;
                if (errorCode?.includes('expired') || errorCode?.includes('invalid') || sessionError.message?.includes('expired')) {
                  setMessage('This password reset link has expired. Password reset links are valid for 1 hour. Please request a new password reset.');
                } else {
                  setMessage('Invalid or expired reset link. Please request a new password reset.');
                }
                
                // Sign out any partial session
                await supabase.auth.signOut();
                return;
              }
              
              if (session && session.user) {
                console.log('✅ Token verified successfully');
                setHasActiveSession(true);
      setIsValidToken(true);
                return;
              }
            }
          }
          
          // If we got here, token format is present but verification failed
          setIsValidToken(true); // Still allow user to try, handleResetPassword will catch errors
        } catch (error) {
          console.error('❌ Error verifying token:', error);
          setIsValidToken(false);
          setMessage('Invalid or expired reset link. Please request a new password reset.');
        }
    } else {
      console.warn('⚠️ No reset password token found in URL');
        
        // Check if user has an active session (might have been logged in from elsewhere)
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user) {
          console.log('✅ User has active session, allowing password reset');
          setHasActiveSession(true);
          setIsValidToken(true);
          setMessage('');
        } else {
      setIsValidToken(false);
      setMessage('Invalid or expired reset link. Please request a new password reset.');
    }
      }
    };
    
    verifyResetToken();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      console.log('🔐 Resetting password...');
      
      // Check if user has an active session (either from token or already logged in)
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        // Try to set session from URL token if available
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        // Extract token from hash
        const hashMatch = hash.match(/#([^#]*)$/);
        if (hashMatch && hashMatch[1]) {
          const params = new URLSearchParams(hashMatch[1]);
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          
          if (accessToken && refreshToken) {
            // Set the session first
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (sessionError) {
              console.error('❌ Session error:', sessionError);
                const errorCode = (sessionError as any).code || sessionError.message;
                if (errorCode?.includes('expired') || errorCode?.includes('invalid') || sessionError.message?.includes('expired')) {
                  setMessage('This password reset link has expired. Password reset links are valid for 1 hour. Please request a new password reset.');
                } else {
              setMessage('Invalid or expired reset link. Please request a new password reset.');
                }
              setLoading(false);
              return;
            }
          }
        }
        } else {
          // No token and no session
          setMessage('No valid session found. Please request a new password reset link.');
          setLoading(false);
          return;
        }
      }

      // Now update the password (user is authenticated either way)
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        console.error('❌ Password update error:', error);
        const errorCode = (error as any).code || error.message;
        if (errorCode?.includes('expired') || errorCode?.includes('invalid') || error.message?.includes('expired')) {
          setMessage('This password reset link has expired. Password reset links are valid for 1 hour. Please request a new password reset.');
        } else {
        setMessage(error.message || 'Failed to reset password. Please try again.');
        }
      } else {
        console.log('✅ Password reset successful');
        setMessage('Password reset successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      console.error('❌ Password reset exception:', error);
      setMessage('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isValidToken === null) {
    return (
      <div className="auth-screen">
        <div className="auth-container">
          <div className="loading-screen">
            <div className="loading-logo">
              <img src="logo512.png" alt="Shikhun Italian" />
            </div>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="auth-screen">
        <div className="auth-container">
          <div className="auth-header">
            <div className="logo">
              <img src="logo512.png" alt="Shikhun Italian" />
            </div>
            <h1>Invalid Reset Link</h1>
          </div>

          <div className="text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <p className="text-gray-600 mb-8" style={{ lineHeight: '1.6', fontSize: '1rem', padding: '0 10px', maxWidth: '100%' }}>
              {message || 'This password reset link is invalid or has expired.'}
            </p>
            <div style={{ marginBottom: '32px' }}>
              <Link 
                to="/forgot-password" 
                className="btn-primary" 
                style={{ display: 'block', textDecoration: 'none', textAlign: 'center' }}
              >
              Request New Reset Link
            </Link>
            </div>
            <div className="auth-footer" style={{ marginTop: '24px' }}>
              <p><Link to="/login">← Back to Login</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <div className="auth-header">
          <div className="logo">
            <img src="logo512.png" alt="Shikhun Italian" />
          </div>
          <h1>Reset Password</h1>
        </div>

        <p className="text-center text-gray-600 mb-6">
          {hasActiveSession 
            ? 'You are logged in. Enter your new password below to update it.'
            : 'Enter your new password below.'}
        </p>

        <form onSubmit={handleResetPassword} className="auth-form">
          <div className="form-group">
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New Password"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <div className="password-input">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm New Password"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {message && (
            <div className={`message ${message.includes('successful') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p><Link to="/login">← Back to Login</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;

